'use client';
import { type FileToUpload, useBaseFilesUpload } from '@capsicum/common';
import { type ChangeEvent, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';

export type UploadProps = {
	maxSize: number;
	selectionLimit: number;
};

const IMAGE_OR_PDF_PREFIX = /^data:(image\/\w+|application\/pdf);base64,/;

type UseFileUploadProps = {
	onUploadedFiles: (files: Array<FileToUpload>) => void;
	uploadProps: UploadProps;
};

const readFileAsBase64 = (file: File): Promise<string> => {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();

		reader.onload = event => {
			if (event.target && event.target.result) {
				resolve(event.target.result.toString());
			} else {
				reject(new Error('Failed to read the file as Base64.'));
			}
		};
		reader.onerror = error => {
			reject(error);
		};
		reader.readAsDataURL(file);
	});
};

export const useFilesUpload = ({ onUploadedFiles, uploadProps }: UseFileUploadProps) => {
	const { addFiles, alert, isFilesLimitReached, removeAllFiles, removeFile, showAlert, uploadedFiles } =
		useBaseFilesUpload({
			maxSize: uploadProps.maxSize,
			onUploadedFiles,
			selectionLimit: uploadProps.selectionLimit
		});
	const inputRef = useRef<HTMLInputElement | null>(null);

	const uploadFile = async (event: ChangeEvent<HTMLInputElement>) => {
		const selectedFiles = event.target.files;

		if (selectedFiles && selectedFiles.length > 0) {
			const newFiles = await Promise.all(
				Array.from(selectedFiles).map(async (file: File) => {
					const base64Content = await readFileAsBase64(file);
					const strippedBase64Content = base64Content.replace(IMAGE_OR_PDF_PREFIX, '');

					return {
						content: strippedBase64Content,
						id: uuidv4(),
						name: file.name,
						size: file.size,
						type: file.type
					};
				})
			);

			addFiles(newFiles);

			if (inputRef.current) {
				inputRef.current.value = '';
			}
		}
	};

	return {
		alert,
		inputRef,
		isFilesLimitReached,
		removeAllFiles,
		removeFile,
		showAlert,
		uploadedFiles,
		uploadFile
	};
};
